import { RootDispatchType } from "common/store";
import { RootState, ThunkResult } from "common/reducers";
import {
  IUserResponse,
  create,
  list,
  load,
  loadAclValues,
  loadByEmail,
  revokeAccess,
  sendInvitation,
  update,
} from "common/api/user.service";
import { UserActions } from "common/reducers/user.actions";
import { handleErrors } from "common/reducers/Utils";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { AccessActions } from "common/reducers/access.actions";
import { List } from "immutable";
import { IUserData } from "common/records/User";
import { aclByTemplate, userHydratedForSave } from "common/selectors/user.selectors";
import { addFlashAlert } from "common/components/FlashAlert";
import { user } from "common/selectors/user.common.selectors";
import { AuthAsyncActions } from "common/reducers/auth.async.actions";
import { UserParamsRecord } from "common/records/index/UserParams";
import { updatePreference } from "common/api/userPreference.service";
import { IGlobalRequestOptions } from "common/api/Fetcher";

export const UserAsyncActions = {
  getUser: (id: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(UserActions.fetchUser(id));

        const user = await load(id);
        dispatch(UserActions.receiveUser(user));
      } catch (response) {
        dispatch(UserActions.setUserLoaded(id));
        handleErrors(response);
      }
    };
  },
  getCurrentUserByEmail: (email: string): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(UserActions.fetchCurrentUser(email));

        const response = await loadByEmail(email);

        dispatch(AuthAsyncActions.changeAccessUid(response.user.current_access_uid || ""));
        dispatch(UserActions.setCurrentUser(response.user));
      } catch (response) {
        dispatch(UserActions.removeCurrentUser());
        const errors = handleErrors(response);
        dispatch(UserActions.setCurrentUserErrors(errors));
      }
    };
  },
  listUsers: (
    queryParams: UserParamsRecord,
    modelName = "users",
    options?: IGlobalRequestOptions,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel(modelName, queryParams));
      try {
        const { users, meta } = await list(queryParams, options);
        const ids = List(users?.map((user: IUserData) => user.id));

        dispatch(UserActions.receiveUsers(users));
        dispatch(IdsManagementActions.receiveModel(modelName, ids, meta?.pagination));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      }
    };
  },
  saveOrCreate: (userId: number, sendInvitation = false, savePref = false): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      dispatch(UserActions.fetchUser(userId));
      const user = userHydratedForSave(getState(), { userId });

      if (!user) {
        return;
      }

      try {
        let result: IUserResponse;
        if (userId <= 0) {
          result = await create(user);
          dispatch(UserActions.receiveUser({ id: -1 } as any));
        } else {
          result = await update(user);
          if (user?.preferences && savePref) {
            const { user_preference } = await updatePreference(user.id, user.preferences);
            result.user.preferences = user_preference;
            dispatch(UserActions.setCurrentUser(result.user));
          }
        }

        dispatch(AccessActions.clearAccesses());
        dispatch(UserActions.receiveUser(result.user));

        if (sendInvitation) {
          dispatch(UserAsyncActions.sendInvitation(result.user.id, result.user.email));
        }

        addFlashAlert("success", `User saved: ${user.email}`);
      } catch (err) {
        handleErrors(err);
        dispatch(UserActions.setUserLoaded(userId));
      }
    };
  },

  sendInvitation: (userId: number, email: string): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(UserActions.fetchUser(userId));

      try {
        await sendInvitation(email);
        addFlashAlert("success", `Invitation sent for ${email}`);
      } catch (err) {
        handleErrors(err);
      } finally {
        dispatch(UserActions.setUserLoaded(userId));
      }
    };
  },
  loadAllowedAcl: (
    userId: number,
    accessUid: string,
    template: string,
    accessId?: number,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      dispatch(IdsManagementActions.fetchModel("allowedAcl"));

      try {
        const response = await loadAclValues(userId, accessUid, template);
        dispatch(UserActions.receiveAllowedAcl(userId, accessUid, template, response));

        // If there is an accessId, set the acl for the access
        if (accessId) {
          const acl = aclByTemplate(getState(), { userId, accessUid, template });
          dispatch(AccessActions.setAccessAcl(accessId, acl));
        }

        dispatch(IdsManagementActions.setModelLoaded("allowedAcl"));
      } catch (err) {
        dispatch(UserActions.receiveAllowedAcl(userId, accessUid, template, { acl: {}, templates: [] }));
        dispatch(IdsManagementActions.setModelLoaded("allowedAcl"));
        handleErrors(err);
      }
    };
  },
  revokeAccess: (userId: number, accessId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      dispatch(UserActions.fetchUser(userId));

      const userRecord = user(getState(), { userId });
      try {
        await revokeAccess(userId, accessId);

        dispatch(IdsManagementActions.removeId(userId, "users"));
        addFlashAlert("success", `Access revoked for ${userRecord?.email}`);
      } catch (err) {
        handleErrors(err);
      } finally {
        dispatch(UserActions.setUserLoaded(userId));
      }
    };
  },
};
